<script setup lang="ts">
import { computed, ref, useSlots } from "vue";

import ExpandArrow from "@/components/ui/ExpandArrow/ExpandArrow.vue";

const props = defineProps<{ isCollapsable?: boolean }>();
const slots = useSlots();

const collapsed = ref(true);

const showCollapsable = computed(() => props.isCollapsable && slots.default);

const toggleCollapsed = () => {
  collapsed.value = !collapsed.value;
};

const handleHeaderClick = () => {
  if (showCollapsable.value) {
    toggleCollapsed();
  }
};

const showContent = computed(() => (showCollapsable.value ? !collapsed.value : true));
</script>

<template>
  <li class="list-group">
    <component
      :is="showCollapsable ? 'button' : 'div'"
      v-if="$slots.header"
      :class="['header', { collapsable: showCollapsable }]"
      @click="handleHeaderClick"
    >
      <!-- Collapsable arrow -->
      <ExpandArrow v-if="showCollapsable" class="arrow" :expanded="!collapsed" />
      <slot name="header" />
    </component>

    <!-- List (might be collapsed) -->
    <Transition>
      <ul v-if="showContent" class="list">
        <!-- List items -->
        <slot />
      </ul>
    </Transition>
  </li>
</template>

<style lang="scss" scoped>
@use "@/components/ui/ComplexList/complexList";
@use "@/styles/variables/colors";

.list-group {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  border-radius: 6px;

  .header {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-grow: 1;

    &.collapsable {
      cursor: pointer;

      &:hover {
        background-color: colors.$background-grey-light;
      }

      &:active {
        background-color: colors.$background-grey;
      }
    }

    .arrow {
      margin-left: 16px;
      color: colors.$icon-subtle;
      margin-right: -4px;
    }
  }

  .list {
    @include complexList.collapsable;

    margin: 0;
    padding-left: 0;
  }
}
</style>
